import {
  CD_BASE, CD_SPIN, CD_S_HALF, CD_QUAD,
  CD_PEAK, CD_PEAK_S, CD_PEAK_W,
  CL_MAX, CL_S_HALF, CL_QUAD,
  ASCENT_DAMP, ASCENT_DAMP_S_REF,
  DESCENT_DAMP, DESCENT_DAMP_S_REF,
  BALL_RADIUS,
} from '../constants';

/**
 * Compute the dimensionless spin parameter S.
 * S = (omega_rad * R) / airspeed
 */
export function spinFactor(spinRateRpm: number, airspeed: number): number {
  const omegaRad = spinRateRpm * (2 * Math.PI / 60);
  return (omegaRad * BALL_RADIUS) / Math.max(airspeed, 0.01);
}

/**
 * Drag coefficient as a function of spin factor.
 * Cd(S) = Cd_base + Cd_spin * S/(S+S_half) + Cd_quad * S² + Cd_peak * gaussian(S)
 *
 * Four-term model:
 *   1. CD_BASE: turbulent drag at zero spin for dimpled ball
 *   2. Michaelis-Menten spin-drag: saturating spin contribution
 *   3. Quadratic spin-drag (CD_QUAD * S²): turbulent wake interaction
 *   4. Gaussian peaked drag: boundary layer transition effect centered
 *      on mid-irons (S≈0.20). Adds targeted drag where dimple
 *      effectiveness changes most rapidly. Negligible for driver
 *      (S=0.08: +0.003) and wedges (S>0.45: ~0), significant for
 *      5-iron (S=0.20: +0.025) and 7-iron (S=0.30: +0.007).
 */
export function getCd(s: number): number {
  const base = CD_BASE + CD_SPIN * (s / (s + CD_S_HALF)) + CD_QUAD * s * s;
  // Gaussian peaked drag centered on mid-iron spin range
  const dS = s - CD_PEAK_S;
  const peaked = CD_PEAK * Math.exp(-(dS * dS) / (2 * CD_PEAK_W * CD_PEAK_W));
  return base + peaked;
}

/**
 * Lift coefficient as a function of spin factor.
 * Cl(S) = Cl_max * S / (S + S_half) - Cl_quad * S²
 *
 * With CL_QUAD=0 this is standard Michaelis-Menten.
 * CL_QUAD can optionally add quadratic dampening at high spin.
 */
export function getCl(s: number): number {
  return CL_MAX * (s / (s + CL_S_HALF)) - CL_QUAD * s * s;
}

/**
 * Apply flight-phase dampening to the lift coefficient.
 *
 * Two phases of dampening model real-world Magnus force reduction:
 *
 * 1. ASCENT DAMPENING (anti-balloon):
 *    During ascent, as the ball decelerates, the spin factor S increases
 *    (same RPM, less airspeed), causing lift to grow. This "ballooning"
 *    inflates apex unrealistically for high-spin clubs. In reality,
 *    progressive wake disruption limits this effect.
 *    Applied linearly with ascent ratio and spin factor.
 *
 * 2. DESCENT DAMPENING:
 *    During descent, the Magnus force has a forward component that prevents
 *    steep landing angles. Real balls experience spin axis precession and
 *    turbulent wake disruption that reduces Magnus effectiveness.
 *    Applied quadratically with descent ratio (stronger at steep descent).
 *
 * @param cl - Base lift coefficient from getCl()
 * @param s - Current spin factor
 * @param vy - Vertical velocity (negative during descent)
 * @param speed - Total airspeed
 * @returns Dampened lift coefficient
 */
export function applyDescentDampening(
  cl: number,
  s: number,
  vy: number,
  speed: number,
): number {
  if (speed < 0.01) return cl;

  if (vy > 0) {
    // ── Ascent phase: anti-balloon dampening ──
    if (ASCENT_DAMP <= 0) return cl;

    const ascentRatio = Math.max(0, vy / speed);
    const spinScale = s / (s + ASCENT_DAMP_S_REF);

    // Linear in ascent angle: proportional reduction as ball climbs steeply
    return cl * (1 - ASCENT_DAMP * spinScale * ascentRatio);
  }

  // ── Descent phase: dampening ──
  if (DESCENT_DAMP <= 0) return cl;

  // How steeply the ball is descending (0 = horizontal, 1 = straight down)
  const descentRatio = Math.max(0, -vy / speed);

  // Spin-dependent scaling: high spin → more dampening
  const spinScale = s / (s + DESCENT_DAMP_S_REF);

  // Quadratic in descent angle: gentle at shallow descent, strong at steep
  return cl * (1 - DESCENT_DAMP * spinScale * descentRatio * descentRatio);
}

/**
 * Compute descent drag boost factor.
 *
 * During descent, increase drag to steepen landing angle and reduce hang time.
 * Unlike lift dampening (which requires spin), drag boost works on all clubs
 * including low-spin woods where there's little lift to dampening.
 *
 * ddMult > 1: more drag during descent → steeper landing, shorter hang
 * ddMult < 1: less drag during descent → shallower landing, longer hang
 *
 * Uses smooth cosine blend from apex (no boost) to full descent (full boost),
 * based on the fraction of flight past apex. The blend uses descent ratio
 * with a square root to apply correction early in descent.
 */
export function descentDragBoost(
  cd: number,
  vy: number,
  speed: number,
  ddMult: number = 1.0,
): number {
  if (speed < 0.01 || vy >= 0 || ddMult === 1.0) return cd;

  // sqrt of descent ratio: applies correction early in descent, maximizing leverage
  const descentRatio = Math.max(0, -vy / speed);
  const blend = Math.sqrt(descentRatio);

  const boostFactor = 1.0 + (ddMult - 1.0) * blend;
  return cd * Math.max(0.3, boostFactor);
}
